import type { ExportPayload, ExportLayout } from './exportTypes';
import { slicePayloadForEmployee } from './exportTypes';
import { ALL_COLUMN_IDS, getColumnDefs } from './exportColumns';

function escapeCsv(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

const BOM = '\uFEFF';

function buildCsvForPayload(payload: ExportPayload): string {
  const lines: string[] = [];
  const cols = getColumnDefs(payload.columns ?? ALL_COLUMN_IDS);
  const colCount = cols.length;

  const formatNum = (n: number) =>
    Math.round(n).toLocaleString(payload.language === 'he' ? 'he-IL' : 'en-US');

  // Title
  lines.push(escapeCsv(payload.title));
  lines.push(escapeCsv(payload.month));

  // Filters
  const filterParts: string[] = [];
  if (payload.filters.status) filterParts.push(payload.filters.status);
  if (payload.filters.hospitalName) filterParts.push(payload.filters.hospitalName);
  if (payload.filters.roleName) filterParts.push(payload.filters.roleName);
  if (filterParts.length > 0) {
    lines.push(escapeCsv(filterParts.join(' | ')));
  }
  lines.push('');

  // Summary totals
  if (payload.rows.length > 1) {
    const summaryParts = payload.aggregated
      ? [
          `Treatments: ${payload.totals.quantity}`,
          `Total: ${formatNum(payload.totals.totalAmount)} ${payload.currencySymbol}`,
        ]
      : [
          `Employees: ${payload.totals.employeeCount}`,
          `Reports: ${payload.totals.reportCount}`,
          `Quantity: ${payload.totals.quantity}`,
          `Total: ${formatNum(payload.totals.totalAmount)} ${payload.currencySymbol}`,
        ];
    lines.push(summaryParts.join(','));
    lines.push('');
  }

  // Per-employee groups
  for (const row of payload.rows) {
    // Group header
    const emptyPadding = Array(Math.max(0, colCount - 2)).fill('').join(',');
    lines.push(
      [
        escapeCsv(
          `${row.employeeName}${row.employmentRole ? ` - ${row.employmentRole}` : ''} (${payload.aggregated ? row.totalQuantity : row.totalReports})`,
        ),
        emptyPadding,
        escapeCsv(`${formatNum(row.totalAmount)} ${payload.currencySymbol}`),
      ].join(','),
    );

    // Table header
    lines.push(cols.map(c => c.header).join(','));

    // Report rows
    for (const r of row.reports) {
      lines.push(
        cols.map(c => {
          const val = c.getValue(r);
          if (c.id === 'amount') {
            return escapeCsv(`${formatNum(r.amount)} ${payload.currencySymbol}`);
          }
          return typeof val === 'number' ? String(val) : escapeCsv(String(val));
        }).join(','),
      );
    }

    // Allowance rows
    for (const a of row.allowances) {
      const allowanceCells = cols.map(c => {
        switch (c.id) {
          case 'date': return 'Allowance';
          case 'reportType': return escapeCsv(a.name);
          case 'hospital': return escapeCsv(a.period);
          case 'quantity': return '1';
          case 'ratePerUnit': return String(a.amount);
          case 'amount': return escapeCsv(`${formatNum(a.amount)} ${payload.currencySymbol}`);
          default: return '';
        }
      });
      lines.push(allowanceCells.join(','));
    }

    // Supervision bonus rows
    for (const sv of row.supervisionBonuses) {
      const svCells = cols.map(c => {
        switch (c.id) {
          case 'date': return 'Supervision Bonus';
          case 'hospital': return escapeCsv(sv.hospitalName);
          case 'quantity': return String(sv.totalUnits);
          case 'ratePerUnit': return String(sv.bonusPerUnit);
          case 'amount': return escapeCsv(`${formatNum(sv.amount)} ${payload.currencySymbol}`);
          default: return '';
        }
      });
      lines.push(svCells.join(','));
    }

    // Employee total
    const totalPadding = Array(Math.max(0, colCount - 2)).fill('').join(',');
    lines.push(
      [
        escapeCsv(payload.aggregated
          ? `Total: ${row.totalQuantity} treatments`
          : `Total: ${row.totalReports} reports`),
        totalPadding,
        escapeCsv(`${formatNum(row.totalAmount)} ${payload.currencySymbol}`),
      ].join(','),
    );

    lines.push('');
  }

  // Grand total
  if (payload.rows.length > 1) {
    const grandPadding = Array(Math.max(0, colCount - 2)).fill('').join(',');
    lines.push(
      [
        escapeCsv(
          payload.aggregated
            ? `Total (${payload.totals.employeeCount} hospitals, ${payload.totals.quantity} treatments)`
            : `Total (${payload.totals.employeeCount} employees, ${payload.totals.reportCount} reports)`,
        ),
        grandPadding,
        escapeCsv(`${formatNum(payload.totals.totalAmount)} ${payload.currencySymbol}`),
      ].join(','),
    );
  }

  lines.push('');
  lines.push(escapeCsv(`Generated: ${payload.generatedAt}`));

  return lines.join('\n');
}

export async function exportCsv(
  payload: ExportPayload,
  filename: string,
  layout: ExportLayout = 'combined',
): Promise<void> {
  if (layout === 'separate-files') {
    const JSZip = (await import('jszip')).default;
    const zip = new JSZip();

    for (const row of payload.rows) {
      const singlePayload = slicePayloadForEmployee(payload, row);
      const safeName = row.employeeName.replace(/[/\\?%*:|"<>]/g, '-');
      zip.file(`${safeName}.csv`, BOM + buildCsvForPayload(singlePayload));
    }

    const blob = await zip.generateAsync({ type: 'blob' });
    downloadBlob(blob, `${filename}.zip`);
    return;
  }

  // combined and per-employee are the same structure for CSV
  const csvContent = BOM + buildCsvForPayload(payload);
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  downloadBlob(blob, `${filename}.csv`);
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
